import React, { useState, useEffect } from 'react';
import { auth } from '../utils/firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../utils/firebase';
import PasswordChangeModal from '../components/PasswordChangeModal';
import Header from '../components/Header';
import { 
  createCreatorCheckout, 
  createBillingPortal, 
  hasActiveCreatorSubscription,
  getSubscriptionStatus 
} from '../utils/stripeClient';

const AccountSettings = () => {
  const [user, setUser] = useState(null);
  const [showPasswordChangeModal, setShowPasswordChangeModal] = useState(false);
  const [activeTab, setActiveTab] = useState('billing');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Billing state
  const [billingInfo, setBillingInfo] = useState({
    firstName: '',
    lastName: '',
    company: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'US',
    phone: ''
  });
  
  // Payment methods state
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [newPaymentMethod, setNewPaymentMethod] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardholderName: ''
  });
  
  // Subscription state
  const [subscription, setSubscription] = useState({
    plan: 'free',
    status: 'active',
    nextBilling: null,
    amount: 0
  });
  
  // Creator subscription state
  const [creatorSubscription, setCreatorSubscription] = useState(null);
  const [isCreator, setIsCreator] = useState(false);
  const [stripeLoading, setStripeLoading] = useState(false);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        loadUserData(currentUser.uid);
      } else {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const loadUserData = async (userId) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        const data = userDoc.data();
        
        // Load billing info if it exists
        if (data.billingInfo) {
          setBillingInfo(data.billingInfo);
        }
        
        // Load subscription info if it exists
        if (data.subscription) {
          setSubscription(data.subscription);
        }
        
        // Mock payment methods (in real app, this would come from a payment processor)
        setPaymentMethods([
          {
            id: '1',
            type: 'visa',
            last4: '4242',
            expiry: '12/25',
            isDefault: true
          }
        ]);
      }

      // Load creator subscription status
      await loadCreatorSubscription();
      
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load creator subscription data
  const loadCreatorSubscription = async () => {
    try {
      const [creatorStatus, subscriptionDetails] = await Promise.all([
        hasActiveCreatorSubscription(),
        getSubscriptionStatus()
      ]);
      
      setIsCreator(creatorStatus);
      setCreatorSubscription(subscriptionDetails);
    } catch (error) {
      console.error('Error loading creator subscription:', error);
      setIsCreator(false);
      setCreatorSubscription(null);
    }
  };

  const saveBillingInfo = async () => {
    setSaving(true);
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        billingInfo: billingInfo,
        updatedAt: new Date()
      });
      alert('Billing information saved successfully!');
    } catch (error) {
      console.error('Error saving billing info:', error);
      alert('Error saving billing information. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const addPaymentMethod = () => {
    // In a real app, this would integrate with Stripe or another payment processor
    const newMethod = {
      id: Date.now().toString(),
      type: 'visa',
      last4: newPaymentMethod.cardNumber.slice(-4),
      expiry: newPaymentMethod.expiryDate,
      isDefault: paymentMethods.length === 0
    };
    
    setPaymentMethods([...paymentMethods, newMethod]);
    setNewPaymentMethod({
      cardNumber: '',
      expiryDate: '',
      cvv: '',
      cardholderName: ''
    });
  };

  // Stripe billing functions
  const handleStartCreatorSubscription = async () => {
    setStripeLoading(true);
    try {
      const checkoutUrl = await createCreatorCheckout();
      window.location.href = checkoutUrl;
    } catch (error) {
      console.error('Error starting creator subscription:', error);
      alert('Failed to start subscription process. Please try again.');
    } finally {
      setStripeLoading(false);
    }
  };

  const handleManageBilling = async () => {
    setStripeLoading(true);
    try {
      const portalUrl = await createBillingPortal();
      window.location.href = portalUrl;
    } catch (error) {
      console.error('Error accessing billing portal:', error);
      alert('Failed to access billing portal. Please try again.');
    } finally {
      setStripeLoading(false);
    }
  };

  const removePaymentMethod = (id) => {
    setPaymentMethods(paymentMethods.filter(method => method.id !== id));
  };

  const setDefaultPaymentMethod = (id) => {
    setPaymentMethods(paymentMethods.map(method => ({
      ...method,
      isDefault: method.id === id
    })));
  };



  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 text-white p-6">
        <div className="max-w-4xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-700 rounded w-1/4 mb-6"></div>
            <div className="h-64 bg-gray-700 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <Header />
      <div className="max-w-4xl mx-auto p-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Account Settings</h1>
          <p className="text-gray-400">Manage your billing, payment methods, and account preferences</p>
        </div>

        {/* Tab Navigation */}
        <div className="flex space-x-1 mb-8 bg-gray-800 p-1 rounded-lg">
          <button
            onClick={() => setActiveTab('billing')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'billing'
                ? 'bg-green-600 text-white'
                : 'text-gray-300 hover:text-white hover:bg-gray-700'
            }`}
          >
            Billing Info
          </button>
          <button
            onClick={() => setActiveTab('payment')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'payment'
                ? 'bg-green-600 text-white'
                : 'text-gray-300 hover:text-white hover:bg-gray-700'
            }`}
          >
            Payment Methods
          </button>
          <button
            onClick={() => setActiveTab('subscription')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'subscription'
                ? 'bg-green-600 text-white'
                : 'text-gray-300 hover:text-white hover:bg-gray-700'
            }`}
          >
            Pricing
          </button>
          <button
            onClick={() => setActiveTab('security')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'security'
                ? 'bg-green-600 text-white'
                : 'text-gray-300 hover:text-white hover:bg-gray-700'
            }`}
          >
            Security
          </button>
        </div>

        {/* Tab Content */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          {/* Billing Information Tab */}
          {activeTab === 'billing' && (
            <div>
              <h2 className="text-xl font-semibold mb-6">Billing Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">First Name</label>
                  <input
                    type="text"
                    value={billingInfo.firstName}
                    onChange={(e) => setBillingInfo({...billingInfo, firstName: e.target.value})}
                    className="w-full p-3 bg-gray-700 border border-gray-600 rounded-md text-white focus:border-green-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Last Name</label>
                  <input
                    type="text"
                    value={billingInfo.lastName}
                    onChange={(e) => setBillingInfo({...billingInfo, lastName: e.target.value})}
                    className="w-full p-3 bg-gray-700 border border-gray-600 rounded-md text-white focus:border-green-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Company (Optional)</label>
                  <input
                    type="text"
                    value={billingInfo.company}
                    onChange={(e) => setBillingInfo({...billingInfo, company: e.target.value})}
                    className="w-full p-3 bg-gray-700 border border-gray-600 rounded-md text-white focus:border-green-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Phone</label>
                  <input
                    type="tel"
                    value={billingInfo.phone}
                    onChange={(e) => setBillingInfo({...billingInfo, phone: e.target.value})}
                    className="w-full p-3 bg-gray-700 border border-gray-600 rounded-md text-white focus:border-green-500 focus:outline-none"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-2">Address</label>
                  <input
                    type="text"
                    value={billingInfo.address}
                    onChange={(e) => setBillingInfo({...billingInfo, address: e.target.value})}
                    className="w-full p-3 bg-gray-700 border border-gray-600 rounded-md text-white focus:border-green-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">City</label>
                  <input
                    type="text"
                    value={billingInfo.city}
                    onChange={(e) => setBillingInfo({...billingInfo, city: e.target.value})}
                    className="w-full p-3 bg-gray-700 border border-gray-600 rounded-md text-white focus:border-green-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">State</label>
                  <input
                    type="text"
                    value={billingInfo.state}
                    onChange={(e) => setBillingInfo({...billingInfo, state: e.target.value})}
                    className="w-full p-3 bg-gray-700 border border-gray-600 rounded-md text-white focus:border-green-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">ZIP Code</label>
                  <input
                    type="text"
                    value={billingInfo.zipCode}
                    onChange={(e) => setBillingInfo({...billingInfo, zipCode: e.target.value})}
                    className="w-full p-3 bg-gray-700 border border-gray-600 rounded-md text-white focus:border-green-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Country</label>
                  <select
                    value={billingInfo.country}
                    onChange={(e) => setBillingInfo({...billingInfo, country: e.target.value})}
                    className="w-full p-3 bg-gray-700 border border-gray-600 rounded-md text-white focus:border-green-500 focus:outline-none"
                  >
                    <option value="US">United States</option>
                    <option value="CA">Canada</option>
                    <option value="UK">United Kingdom</option>
                    <option value="AU">Australia</option>
                  </select>
                </div>
              </div>
              <div className="mt-6">
                <button
                  onClick={saveBillingInfo}
                  disabled={saving}
                  className="px-6 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white rounded-md font-medium transition-colors"
                >
                  {saving ? 'Saving...' : 'Save Billing Information'}
                </button>
              </div>
            </div>
          )}

          {/* Payment Methods Tab */}
          {activeTab === 'payment' && (
            <div>
              <h2 className="text-xl font-semibold mb-6">Payment Methods</h2>
              
              {/* Existing Payment Methods */}
              <div className="mb-8">
                <h3 className="text-lg font-medium mb-4">Saved Payment Methods</h3>
                {paymentMethods.map((method) => (
                  <div key={method.id} className="flex items-center justify-between p-4 bg-gray-700 rounded-lg mb-3 border border-gray-600">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-8 bg-blue-600 rounded flex items-center justify-center text-white font-bold text-sm">
                        {method.type.toUpperCase()}
                      </div>
                      <div>
                        <p className="text-white font-medium">•••• •••• •••• {method.last4}</p>
                        <p className="text-gray-400 text-sm">Expires {method.expiry}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {method.isDefault && (
                        <span className="px-2 py-1 bg-green-600 text-white text-xs rounded">Default</span>
                      )}
                      {!method.isDefault && (
                        <button
                          onClick={() => setDefaultPaymentMethod(method.id)}
                          className="text-green-400 hover:text-green-300 text-sm"
                        >
                          Set Default
                        </button>
                      )}
                      <button
                        onClick={() => removePaymentMethod(method.id)}
                        className="text-red-400 hover:text-red-300 text-sm"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Add New Payment Method */}
              <div className="border-t border-gray-600 pt-6">
                <h3 className="text-lg font-medium mb-4">Add New Payment Method</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Card Number</label>
                    <input
                      type="text"
                      value={newPaymentMethod.cardNumber}
                      onChange={(e) => setNewPaymentMethod({...newPaymentMethod, cardNumber: e.target.value})}
                      placeholder="1234 5678 9012 3456"
                      className="w-full p-3 bg-gray-700 border border-gray-600 rounded-md text-white focus:border-green-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Cardholder Name</label>
                    <input
                      type="text"
                      value={newPaymentMethod.cardholderName}
                      onChange={(e) => setNewPaymentMethod({...newPaymentMethod, cardholderName: e.target.value})}
                      className="w-full p-3 bg-gray-700 border border-gray-600 rounded-md text-white focus:border-green-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Expiry Date</label>
                    <input
                      type="text"
                      value={newPaymentMethod.expiryDate}
                      onChange={(e) => setNewPaymentMethod({...newPaymentMethod, expiryDate: e.target.value})}
                      placeholder="MM/YY"
                      className="w-full p-3 bg-gray-700 border border-gray-600 rounded-md text-white focus:border-green-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">CVV</label>
                    <input
                      type="text"
                      value={newPaymentMethod.cvv}
                      onChange={(e) => setNewPaymentMethod({...newPaymentMethod, cvv: e.target.value})}
                      placeholder="123"
                      className="w-full p-3 bg-gray-700 border border-gray-600 rounded-md text-white focus:border-green-500 focus:outline-none"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <button
                    onClick={addPaymentMethod}
                    className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-md font-medium transition-colors"
                  >
                    Add Payment Method
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Pricing & Usage Tab (formerly Subscription) */}
          {activeTab === 'subscription' && (
            <div>
              <h2 className="text-xl font-semibold mb-6">Pricing & Usage</h2>
              
              {/* Pay-Per-Use Model Info */}
              <div className="bg-gray-700 rounded-lg p-6 mb-6 border border-gray-600">
                <h3 className="text-lg font-medium mb-4 text-white">💳 Pay-Per-Use Model</h3>
                <p className="text-gray-300 mb-4">
                  No subscriptions required. Pay only when you use features.
                </p>
              </div>

              {/* Created Game Launches */}
              <div className="bg-gradient-to-r from-green-600 to-blue-600 rounded-lg p-6 mb-6 border border-green-500">
                <h3 className="text-lg font-medium mb-4 text-white">🚀 Launch Your Created Games</h3>
                <div className="text-center mb-4">
                  <div className="text-4xl font-bold text-white mb-2">$15</div>
                  <div className="text-green-100">per launch</div>
                </div>
                <div className="bg-green-700 rounded-lg p-4 mb-4">
                  <h4 className="text-white font-semibold mb-2">What's Included</h4>
                  <ul className="text-green-100 text-sm space-y-1">
                    <li>✓ 12-hour launch window</li>
                    <li>✓ Full game access</li>
                    <li>✓ Professional hosting tools</li>
                    <li>✓ Analytics and tracking</li>
                  </ul>
                </div>
                <a
                  href="/my-games"
                  className="block w-full bg-white text-green-600 font-semibold py-3 px-6 rounded-lg hover:bg-green-50 transition-colors text-center"
                >
                  Go to My Games
                </a>
              </div>

              {/* Marketplace Games */}
              <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg p-6 mb-6 border border-purple-500">
                <h3 className="text-lg font-medium mb-4 text-white">🛒 Purchase Marketplace Games</h3>
                <div className="text-center mb-4">
                  <div className="text-4xl font-bold text-white mb-2">$35+</div>
                  <div className="text-purple-100">base price + $15 launch fee</div>
                </div>
                <div className="bg-purple-700 rounded-lg p-4 mb-4">
                  <h4 className="text-white font-semibold mb-2">What's Included</h4>
                  <ul className="text-purple-100 text-sm space-y-1">
                    <li>✓ Full game access</li>
                    <li>✓ 1 launch right (12-hour window)</li>
                    <li>✓ Can repurchase to launch again</li>
                    <li>✓ Support from game creator</li>
                  </ul>
                </div>
                <a
                  href="/marketplace"
                  className="block w-full bg-white text-purple-600 font-semibold py-3 px-6 rounded-lg hover:bg-purple-50 transition-colors text-center"
                >
                  Browse Marketplace
                </a>
              </div>

              {/* Free Features */}
              <div className="bg-gray-700 rounded-lg p-6 border border-gray-600">
                <h3 className="text-lg font-medium mb-4 text-white">✨ Always Free</h3>
                <ul className="text-gray-300 space-y-2">
                  <li className="flex items-start">
                    <span className="text-green-400 mr-2">✓</span>
                    <span>Create unlimited games</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-400 mr-2">✓</span>
                    <span>Submit games to marketplace (unlimited)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-400 mr-2">✓</span>
                    <span>Access to analytics and tools</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-400 mr-2">✓</span>
                    <span>Team management features</span>
                  </li>
                </ul>
              </div>

              {/* Legacy Subscription Info (if exists) */}
              {isCreator && creatorSubscription && (
                <div className="bg-yellow-900/30 border border-yellow-600 rounded-lg p-4 mt-6">
                  <p className="text-yellow-200 text-sm">
                    <strong>Note:</strong> You have a legacy subscription active. This will continue to work until it expires.
                    For new features, the platform uses pay-per-use pricing.
                  </p>
                </div>
              )}




            </div>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <div>
              <h2 className="text-xl font-semibold mb-6">Security Settings</h2>
              
              <div className="space-y-6">
                <div className="bg-gray-700 rounded-lg p-6 border border-gray-600">
                  <h3 className="text-lg font-medium mb-4">Change Password</h3>
                  <p className="text-gray-400 mb-4">Update your account password for enhanced security</p>
                  <button
                    onClick={() => setShowPasswordChangeModal(true)}
                    className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-md font-medium transition-colors"
                  >
                    🔑 Change Password
                  </button>
                </div>

                <div className="bg-gray-700 rounded-lg p-6 border border-gray-600">
                  <h3 className="text-lg font-medium mb-4">Two-Factor Authentication</h3>
                  <p className="text-gray-400 mb-4">Add an extra layer of security to your account</p>
                  <button className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium transition-colors">
                    Enable 2FA
                  </button>
                </div>

                <div className="bg-gray-700 rounded-lg p-6 border border-gray-600">
                  <h3 className="text-lg font-medium mb-4">Login History</h3>
                  <p className="text-gray-400 mb-4">Recent login activity for your account</p>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-3 bg-gray-600 rounded">
                      <div>
                        <p className="text-white font-medium">Current Session</p>
                        <p className="text-gray-400 text-sm">Chrome on Mac • Just now</p>
                      </div>
                      <span className="text-green-400 text-sm">Active</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-gray-600 rounded">
                      <div>
                        <p className="text-white font-medium">Previous Session</p>
                        <p className="text-gray-400 text-sm">Safari on iPhone • 2 days ago</p>
                      </div>
                      <span className="text-gray-400 text-sm">Ended</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Password Change Modal */}
      <PasswordChangeModal
        isOpen={showPasswordChangeModal}
        onClose={() => setShowPasswordChangeModal(false)}
        onSuccess={() => {
          alert('✅ Password updated successfully!');
          setShowPasswordChangeModal(false);
        }}
      />
    </div>
  );
};

export default AccountSettings;
